/**
 * HOF-696 D5/D6 — where a seat lands, and whether it may open a farm app.
 *
 * Every predicate here reads the seat's `active_farm.permissions` (and, for farm
 * apps, `active_farm.apps`) — never a role name. A role is a bundle the backend
 * may reshape; the permission is the contract (ROLE_AND_ACCESS_MODEL.md).
 *
 * Pure, no React: the login page, the root redirect, the passkey prompt and the
 * layouts all call the same function on the profile they actually fetched.
 */

/** The shape these helpers read; `ActiveFarm` from AuthContext satisfies it. */
export interface SeatLike {
  permissions?: readonly string[] | null;
  apps?: readonly string[] | null;
}

export const LABOUR_PATH = '/apps/labour';
export const DEFAULT_LANDING = '/documents';

export function hasSeatPermission(farm: SeatLike | null | undefined, permission: string): boolean {
  return Array.isArray(farm?.permissions) && farm.permissions.includes(permission);
}

/**
 * A labour seat without the farm — the farm's workers (`worker`) and their
 * Vorarbeiter (`foreman`, HOF-697, who may also read the team's labour rows). It
 * has no `farm.read`, so every page of the normal shell would answer 403; it gets
 * the worker shell instead.
 */
export function isSelfOnlyLabourSeat(farm: SeatLike | null | undefined): boolean {
  return hasSeatPermission(farm, 'labour.self.read') && !hasSeatPermission(farm, 'farm.read');
}

/**
 * The one landing rule. `/apps/labour` for a self-only labour seat — whether or
 * not the farm has the app switched on, because the app page is where such a
 * seat is told to see the office; `/documents` for everyone else.
 */
export function landingPath(farm: SeatLike | null | undefined): string {
  return isSelfOnlyLabourSeat(farm) ? LABOUR_PATH : DEFAULT_LANDING;
}

/** The profile fields the landing reads; `UserProfile` from AuthContext satisfies it. */
export interface ProfileLike {
  active_farm?: SeatLike | null;
  labour_access_ended?: boolean | null;
}

/**
 * HOF-696 D12 — the person holds no active seat because a worker seat ended (the
 * labour access sync suspended it; `/auth/me` → `labour_access_ended`). Read
 * strictly — anything but `true`, including a backend that predates the field,
 * is false — and never while an active seat exists.
 */
export function isLabourAccessEnded(profile: ProfileLike | null | undefined): boolean {
  return !profile?.active_farm && profile?.labour_access_ended === true;
}

/**
 * The landing for a whole profile: `landingPath` of its seat, except that a worker
 * whose access ended lands on the labour app too — it is where they are told to see
 * the office ("Bitte im Büro melden"); `/documents` would answer them 403. Every
 * landing decision (root redirect, login, passkey prompt) goes through here.
 */
export function landingFor(profile: ProfileLike | null | undefined): string {
  return isLabourAccessEnded(profile) ? LABOUR_PATH : landingPath(profile?.active_farm);
}

/**
 * D6 — a farm app opens when the farm has it active AND the seat holds one of the
 * permissions that open it. Fails closed on a profile without `apps` (a backend
 * from before HOF-696 during a rolling deploy).
 */
export function canOpenFarmApp(
  farm: SeatLike | null | undefined,
  appId: string,
  permissionsAny: readonly string[],
): boolean {
  if (!farm || !Array.isArray(farm.apps) || !farm.apps.includes(appId)) return false;
  return permissionsAny.some((permission) => hasSeatPermission(farm, permission));
}
